/**
 * Domain vocabulary shared by API, UI, worker, gateway and migration.
 * Greek labels for the enumerations live in the UI dictionaries; these are the stored codes.
 */
import { z } from 'zod';
/** The built-in roles. Administrators may add their own (table `roles`); a user's role is any role key. */
export const ROLES = ['admin', 'editor', 'journalist', 'producer', 'director', 'autocue', 'graphics', 'readonly'];
export const ROLE_KEY_RE = /^[a-z][a-z0-9_-]{1,31}$/;
export const BUILT_IN_ROLE_NAMES = {
    admin: 'Διαχειριστής',
    editor: 'Αρχισυντάκτης',
    journalist: 'Δημοσιογράφος',
    producer: 'Παραγωγός',
    director: 'Σκηνοθέτης',
    autocue: 'Autocue',
    graphics: 'Γραφικά',
    readonly: 'Μόνο ανάγνωση',
};
/**
 * Permissions, named after the HPress letters where one exists (A M C D R S Q P) so that
 * administrators coming from HPress recognise them.
 */
export const PERMISSIONS = [
    'story.create', // A
    'story.edit.own', // M
    'story.edit.any', // C
    'story.cut', // D  (remove from a rundown)
    'story.read', // R
    'rundown.reorder', // S  (move, copy bulletin)
    'rundown.open', // Q
    'autocue.send', // P
    'rundown.publish', // new: link/publish a rundown to playout / MOS
    'playout.transport', // new: never granted to editorial roles
    'graphics.take', // new: operator-only
    'admin.users',
    'admin.config',
    'export.pdf',
    // Θεματολόγιο (HPress module THEMATA, group letters R A M C D)
    'topic.read', // R
    'topic.create', // A
    'topic.edit.own', // M
    'topic.edit.any', // C
    'topic.delete', // D
    // Αρχείο Video (HPress module HVID, group letters R A M C D)
    'video.read', // R
    'video.create', // A
    'video.edit.own', // M
    'video.edit.any', // C
    'video.delete', // D
    // the LTO archive (new): ask for an item back from tape; look after the tapes and the restores
    'video.restore',
    'archive.manage',
];
const TOPIC_ALL = ['topic.read', 'topic.create', 'topic.edit.own', 'topic.edit.any', 'topic.delete'];
const VIDEO_ALL = ['video.read', 'video.create', 'video.edit.own', 'video.edit.any', 'video.delete', 'video.restore', 'archive.manage'];
export const ROLE_PERMISSIONS = {
    admin: PERMISSIONS,
    editor: ['story.create', 'story.edit.own', 'story.edit.any', 'story.cut', 'story.read', 'rundown.reorder', 'rundown.open', 'autocue.send', 'rundown.publish', 'export.pdf', ...TOPIC_ALL, ...VIDEO_ALL],
    journalist: ['story.create', 'story.edit.own', 'story.read', 'rundown.open', 'export.pdf', 'topic.read', 'topic.create', 'topic.edit.own', 'video.read', 'video.create', 'video.edit.own', 'video.restore'],
    producer: ['story.create', 'story.edit.own', 'story.edit.any', 'story.cut', 'story.read', 'rundown.reorder', 'rundown.open', 'autocue.send', 'rundown.publish', 'export.pdf', ...TOPIC_ALL, ...VIDEO_ALL],
    director: ['story.read', 'rundown.open', 'export.pdf', 'topic.read', 'video.read'],
    autocue: ['story.read', 'rundown.open', 'autocue.send', 'topic.read', 'video.read'],
    graphics: ['story.read', 'rundown.open', 'graphics.take', 'topic.read', 'video.read'],
    readonly: ['story.read', 'rundown.open', 'topic.read', 'video.read'],
};
/** Αρχείο Video: HPress "Τύπος" of a tape item (Μονταρισμένο / Αμοντάριστο). */
export const VIDEO_TYPES = ['edited', 'raw'];
/** 'HH:MM:SS' timecode (seconds resolution, as HPress stores TC αρχής / TC τέλους) → seconds, or null. */
export function parseTimecodeHms(text) {
    if (!text)
        return null;
    const m = /^(\d{1,2}):([0-5]\d):([0-5]\d)$/.exec(text.trim());
    if (!m)
        return null;
    return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}
export function formatTimecodeHms(seconds) {
    const s = Math.max(0, Math.round(seconds));
    return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
/** Permissions of a built-in role (the defaults); a running system reads the `roles` table instead. */
export function hasPermission(role, p) {
    return ROLE_PERMISSIONS[role].includes(p);
}
export function isBuiltInRole(key) {
    return ROLES.includes(key);
}
/** HPress letter for a permission, for display next to roles. */
export const HPRESS_LETTER = {
    'story.create': 'A',
    'story.edit.own': 'M',
    'story.edit.any': 'C',
    'story.cut': 'D',
    'story.read': 'R',
    'rundown.reorder': 'S',
    'rundown.open': 'Q',
    'autocue.send': 'P',
    'topic.read': 'R',
    'topic.create': 'A',
    'topic.edit.own': 'M',
    'topic.edit.any': 'C',
    'topic.delete': 'D',
    'video.read': 'R',
    'video.create': 'A',
    'video.edit.own': 'M',
    'video.edit.any': 'C',
    'video.delete': 'D',
};
/** Permissions grouped the way the roles screen shows them (one group per HPress module, then the rest). */
export const PERMISSION_GROUPS = [
    { key: 'news', el: 'Ειδήσεις', en: 'News', permissions: ['story.read', 'story.create', 'story.edit.own', 'story.edit.any', 'story.cut', 'rundown.open', 'rundown.reorder', 'autocue.send', 'rundown.publish', 'export.pdf'] },
    { key: 'topics', el: 'Θεματολόγιο', en: 'Topics', permissions: ['topic.read', 'topic.create', 'topic.edit.own', 'topic.edit.any', 'topic.delete'] },
    { key: 'videos', el: 'Αρχείο Video', en: 'Video archive', permissions: ['video.read', 'video.create', 'video.edit.own', 'video.edit.any', 'video.delete', 'video.restore', 'archive.manage'] },
    { key: 'control', el: 'Έλεγχος εκπομπής', en: 'Broadcast control', permissions: ['playout.transport', 'graphics.take'] },
    { key: 'admin', el: 'Διαχείριση', en: 'Administration', permissions: ['admin.users', 'admin.config'] },
];
/** Greek and English names of the permissions, for the roles screen. */
export const PERMISSION_NAMES = {
    'story.read': { el: 'Ανάγνωση ειδήσεων', en: 'Read stories' },
    'story.create': { el: 'Νέα είδηση', en: 'Create stories' },
    'story.edit.own': { el: 'Τροποποίηση δικών του ειδήσεων', en: 'Edit own stories' },
    'story.edit.any': { el: 'Τροποποίηση κάθε είδησης', en: 'Edit any story' },
    'story.cut': { el: 'Αποκοπή από τον οδηγό', en: 'Cut from the rundown' },
    'rundown.open': { el: 'Άνοιγμα οδηγού δελτίου', en: 'Open rundowns' },
    'rundown.reorder': { el: 'Αλλαγή σειράς, αντιγραφή δελτίου', en: 'Reorder, copy bulletin' },
    'autocue.send': { el: 'Αποστολή στο Autocue', en: 'Send to autocue' },
    'rundown.publish': { el: 'Δημοσίευση σε Playout / MOS', en: 'Publish to playout / MOS' },
    'export.pdf': { el: 'Εκτυπώσεις PDF', en: 'PDF exports' },
    'playout.transport': { el: 'Χειρισμός player (ποτέ σε συντακτικούς ρόλους)', en: 'Player transport (never for editorial roles)' },
    'graphics.take': { el: 'TAKE γραφικών', en: 'Graphics TAKE' },
    'admin.users': { el: 'Χρήστες και ρόλοι', en: 'Users and roles' },
    'admin.config': { el: 'Ρυθμίσεις συστήματος', en: 'System settings' },
    'topic.read': { el: 'Ανάγνωση', en: 'Read' },
    'topic.create': { el: 'Νέο θέμα', en: 'Create' },
    'topic.edit.own': { el: 'Τροποποίηση δικών του', en: 'Edit own' },
    'topic.edit.any': { el: 'Τροποποίηση όλων', en: 'Edit any' },
    'topic.delete': { el: 'Διαγραφή', en: 'Delete' },
    'video.read': { el: 'Ανάγνωση', en: 'Read' },
    'video.create': { el: 'Νέο θέμα video', en: 'Create' },
    'video.edit.own': { el: 'Τροποποίηση δικών του', en: 'Edit own' },
    'video.edit.any': { el: 'Τροποποίηση όλων', en: 'Edit any' },
    'video.delete': { el: 'Διαγραφή', en: 'Delete' },
    'video.restore': { el: 'Ανάκτηση από ταινία', en: 'Restore from tape' },
    'archive.manage': { el: 'Διαχείριση ταινιών και ανακτήσεων', en: 'Manage tapes and restores' },
};
/** Programme kinds: HPress distinguishes on-air bulletins, their "-SPK" draft twins and the "*****" holding bulletin. */
export const PROGRAMME_KINDS = ['bulletin', 'speaker', 'holding', 'other'];
/** Item kinds. HPress has none (type is encoded in the title); 'story' is the default and never required. */
export const ITEM_KINDS = ['story', 'separator', 'break', 'studio', 'live', 'phone', 'vo', 'package', 'graphics', 'filler', 'other'];
export const RUNDOWN_STATUSES = ['draft', 'linked', 'published', 'archived'];
export const STORY_STATUSES = ['draft', 'ready', 'approved'];
/** Downstream synchronisation state of a rundown or item towards a target system. */
export const SYNC_STATES = ['unlinked', 'pending', 'synced', 'blocked', 'failed'];
export const INTEGRATION_TARGETS = ['playout', 'mos'];
/** Autocue export profiles. The legacy profile reproduces the HPress file byte for byte. */
export const AUTOCUE_PROFILES = ['hpress-cp1253', 'utf8'];
export const EXPORT_KINDS = ['autocue', 'pdf-list', 'pdf-full'];
// ── zod schemas for API boundaries ─────────────────────────────────────────────
export const uuid = z.uuid();
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const seconds = z.number().int().min(0).max(24 * 3600);
export const storyBodySchema = z.object({
    title: z.string().min(1).max(500),
    bodyHtml: z.string().max(2_000_000),
    categoryId: uuid.nullable().optional(),
    originUserId: uuid.nullable().optional(),
    guideComments: z.string().max(2000).nullable().optional(),
    displayNumber: z.string().max(20).nullable().optional(),
    videoS: seconds.optional(),
    readOverrideS: seconds.nullable().optional(),
    kind: z.enum(ITEM_KINDS).optional(),
    /** Boxes inside boxes: box key (`ON#1`) → the second it comes in inside the box before it (storyline.ts). */
    storylineAt: z
        .record(z.string().min(1).max(80), z.number().int().min(0).max(3_600_000))
        .refine((m) => Object.keys(m).length <= 200, 'at most 200 boxes')
        .optional(),
});
export const createStorySchema = storyBodySchema.extend({
    /** Place the new story in this rundown (HPress: Δελτίο + Ημ/νία Δελτίου on the story form). */
    rundownId: uuid.nullable().optional(),
    programmeId: uuid.nullable().optional(),
    date: isoDate.optional(),
});
export const saveStorySchema = storyBodySchema.partial().extend({
    /** Revision the client last saw; mismatch = conflict (409). */
    baseRevision: z.number().int().min(0),
    /** Client-generated id so that a retried save is idempotent. */
    saveId: uuid.optional(),
});
export const rundownMutationSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('insert'), afterItemId: uuid.nullable(), storyId: uuid.nullable(), kind: z.enum(ITEM_KINDS).default('story'), title: z.string().max(500).optional(), displayNumber: z.string().max(20).nullable().optional() }),
    z.object({ op: z.literal('move'), itemId: uuid, toIndex: z.number().int().min(0) }),
    z.object({ op: z.literal('remove'), itemId: uuid }),
    z.object({ op: z.literal('restore'), itemId: uuid }),
    z.object({ op: z.literal('float'), itemId: uuid, floated: z.boolean() }),
    z.object({ op: z.literal('duplicate'), itemId: uuid, mode: z.enum(['copy', 'linked']) }),
    z.object({ op: z.literal('update'), itemId: uuid, patch: z.object({ displayNumber: z.string().max(20).nullable().optional(), titleOverride: z.string().max(500).nullable().optional(), videoS: seconds.optional(), readOverrideS: seconds.nullable().optional(), kind: z.enum(ITEM_KINDS).optional(), mediaAssetId: z.string().nullable().optional(), mediaInMs: z.number().int().min(0).nullable().optional(), mediaOutMs: z.number().int().min(0).nullable().optional(), channel: z.string().max(40).nullable().optional() }) }),
]);
export const rundownMutationRequestSchema = z.object({
    /** Revision the client last saw. */
    baseRevision: z.number().int().min(0),
    /** Client-generated id; the same id applied twice is a no-op with the same answer. */
    mutationId: uuid,
    mutations: z.array(rundownMutationSchema).min(1).max(200),
});
// ── Storyboard: the cues of a story in playing order, the playlist the newsroom automation will follow ──
export const CUE_KINDS = ['video', 'cg'];
/**
 * AirPost's own CG cue types: what the journalist means by a title. The graphics system's template ids
 * are mapped at the gateway; these names make no claim about any vendor's templates.
 */
export const CG_TEMPLATES = [
    { key: 'lower-third-1', el: 'Φάσα 1 γραμμής', en: 'Lower third, 1 line', lines: 1, defaultMs: 7000 },
    { key: 'lower-third-2', el: 'Φάσα 2 γραμμών', en: 'Lower third, 2 lines', lines: 2, defaultMs: 7000 },
    { key: 'credit', el: 'Ρεπορτάζ / Κάμερα (credit)', en: 'Reporter / camera credit', lines: 2, defaultMs: 5000 },
    { key: 'locator', el: 'Τοποθεσία', en: 'Locator', lines: 1, defaultMs: 6000 },
    { key: 'live', el: 'LIVE', en: 'LIVE bug', lines: 1, defaultMs: 0 },
    { key: 'fullscreen', el: 'Ολοσέλιδο γραφικό', en: 'Full-screen graphic', lines: 4, defaultMs: 10000 },
];
export const CG_TEMPLATE_KEYS = CG_TEMPLATES.map((x) => x.key);
export const cgTemplate = (key) => CG_TEMPLATES.find((x) => x.key === key) ?? null;
/** A heading as the marker scanner compares it: no accents, upper case, one space between words, no trailing colon. */
export function normalizeMarker(text) {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .replace(/[\s:：.\-–—]+$/g, '')
        .trim();
}
/** The headings journalists type in the script that call for a cue (the HPress habit: ΒΙΝΤΕΟ ΜΕ ΗΧΟ, ΤΙΤΛΟΣ 1, ΦΑΣΑ, ΡΕΠΟΡΤΑΖ…). */
export const CUE_MARKERS = [
    { kind: 'video', re: /^(ΒΙΝΤΕΟ|VIDEO|VT|SOT|PKG|ΠΑΚΕΤΟ|\(?ΦΙΛΕΡ\)?)(\s|\d|$)/, template: null },
    { kind: 'cg', re: /^(ΤΙΤΛΟΣ|CG|SUPER|ΥΠΟΤΙΤΛΟΣ|ΛΕΖΑΝΤΑ|ΦΑΣΑ|ΦΑΣΕΣ)(\s|\d|$)/, template: 'lower-third-1' },
    // a statement: the speaker's name and role on a two-line strap
    { kind: 'cg', re: /^ΔΗΛΩΣΗ(\s|\d|$|\()/, template: 'lower-third-2' },
    // a card: a full-screen graphic
    { kind: 'cg', re: /^ΚΑΡΤΑ(\s|\d|$)/, template: 'fullscreen' },
    { kind: 'cg', re: /^(ΡΕΠΟΡΤΑΖ|ΚΑΜΕΡΑ|ΜΟΝΤΑΖ|ΕΙΚΟΝΑ|ΗΧΟΣ|ΗΧΟΛΗΨΙΑ)(\s|$)/, template: 'credit' },
    { kind: 'cg', re: /^(LIVE|ΖΩΝΤΑΝΑ|ΖΩΝΤΑΝΗ ΣΥΝΔΕΣΗ)(\s|$)/, template: 'live' },
];
/** Does this block of the script mark a cue? The kind, the suggested CG template and the normalized label. */
export function detectCueMarker(text) {
    const label = normalizeMarker(text);
    if (!label || label.length > 60)
        return null;
    for (const m of CUE_MARKERS)
        if (m.re.test(label))
            return { kind: m.kind, template: m.template, label };
    return null;
}
/** One cue as the editor sends it; `mediaCode: 'auto'` reserves the next virtual code. */
export const storyboardCueInputSchema = z.object({
    id: uuid.optional(),
    kind: z.enum(CUE_KINDS),
    anchor: z.string().max(80).nullable().optional(),
    label: z.string().max(80).default(''),
    mediaAssetId: z.string().max(200).nullable().optional(),
    mediaCode: z.string().max(40).nullable().optional(),
    inMs: z.number().int().min(0).nullable().optional(),
    outMs: z.number().int().min(0).nullable().optional(),
    template: z.enum(CG_TEMPLATE_KEYS).nullable().optional(),
    lines: z.array(z.string().max(200)).max(6).default([]),
    durationMs: z.number().int().min(0).max(3_600_000).nullable().optional(),
});
export const storyboardPutSchema = z.object({ cues: z.array(storyboardCueInputSchema).max(60) });
// ── User preferences ────────────────────────────────────────────────────────────
/**
 * What a user keeps for themself, stored as JSON on the user row and merged key by key.
 * `homeFeedSources`: the feed sources the start screen shows; null or empty = every source.
 */
export const userPreferencesSchema = z
    .object({
    homeFeedSources: z.array(z.string().uuid()).max(200).nullable(),
})
    .partial();
/** The stored JSON read back as preferences; anything unknown or malformed is dropped. */
export function readUserPreferences(raw) {
    const r = userPreferencesSchema.safeParse(raw ?? {});
    return r.success ? r.data : {};
}
//# sourceMappingURL=domain.js.map